import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ExternalLink, Phone } from "lucide-react";
import CategoryNav from "./CategoryNav";
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';
import FilterChip from './FilterChip';

const VENUE_TYPE_OPTIONS = ["Banquet Hall", "Restaurants"];

const VENUE_FIELDS: FieldDef[] = [
  { key: "name", label: "Venue name", required: true },
  { key: "type", label: "Type", type: "select", options: VENUE_TYPE_OPTIONS },
  { key: "description", label: "Description", type: "textarea" },
  { key: "city", label: "City", placeholder: "Frisco" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  { key: "mapsLink", label: "Google Maps link", type: "url" },
  ...SOCIAL_FIELDS,
];
export interface Venue {
  name: string;
  description?: string;
  type?: string;
  city: string;
  address: string;
  website: string;
  image: string;
  mapsLink?: string;
  contactNumber?: string;
  instagram?: string;
}

// Instagram post where TastebudsByAnubhi featured this venue.
const TASTEBUDS_FEATURE_URL = "https://www.instagram.com/p/DdK4LzasAeV/";

// Venue names featured by TastebudsByAnubhi — shown in their own section above
// the rest of the listings, same pattern as Desserts.tsx / Grocery.tsx.
export const FEATURED_VENUE_NAMES = new Set(["lotus springs ranch"]);

// Photos go in /public/venues/<file>. Until added, the onError fallback shows a
// stock event-hall image so cards never look broken.
export const VENUES: Venue[] = [
  {
    name: "The Dream Ranch, 36 acres of a Picturesque Ranch",
    city: "Wolfe City",
    address: "1052 county rd 4821, wolfe city, TX,75496",
    website: "https://dreamranchevents.com",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-23/410a4e0300d1.png",
    mapsLink: "https://www.google.com/maps/search/?api=1&query=1052+county+rd+4821,+wolfe+city,+TX,75496"
  },
  { name: "Frisco Hall Event Center", city: "Frisco", address: "5353 Independence Pkwy, Ste 1, Frisco, TX 75035", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780828176618-50fikl.png" },
  { name: "Prism LasColinas", city: "Irving", address: "350 E Royal Ln, Suite 123, Irving, TX 75039", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780828325802-fxmwj.png" },
  { name: "Dallas Palms", city: "Carrollton", address: "2424 Marsh Lane, Carrollton, TX 75006", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780828400433-absgml.png" },
  { name: "Grandion Event Venue", city: "Frisco", address: "1810 Parkwood Blvd, Frisco, TX 75034", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780828469148-e155fk.png" },
  { name: "Atithi Venue", city: "Plano", address: "9060 Independence Pkwy, Plano, TX 75025", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780828534540-jx9t8d.png" },
  { name: "The Meridian Venue", city: "Fairview", address: "351 Southwind Ln, Fairview, TX 75069", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780828895279-3vtjls.jpg", mapsLink: "https://google.com/maps?rlz=1C1GCEA_enUS1118US1120&gs_lcrp=EgZjaHJvbWUyBggAEEUYOdIBCDI3MzVqMGo3qAIAsAIA&um=1&ie=UTF-8&fb=1&gl=us&sa=X&geocode=KQs9cG-mEUyGMeKENY6LAOV9&daddr=351+Southwind+Ln,+Fairview,+TX+75069" },
  { name: "Elegance Ballroom & Event Center", city: "Plano", address: "8740 Ohio Dr A1, Plano, TX 75024", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780829334078-kygkyr.jpg", mapsLink: "https://www.google.com/maps?sca_esv=8ffe236e7f802148&rlz=1C1GCEA_enUS1118US1120&sxsrf=ANbL-n7Nu_ioc8LFmJKjRG5ipeGACkT9Pg:1780828646352&uact=5&gs_lp=Egxnd3Mtd2l6LXNlcnAiIEVsZWdhbmNlIEJhbGxyb29tICYgRXZlbnQgQ2VudGVyMg4QLhivARjHARiABBiOBTIGEAAYFhgeMgYQABgWGB4yBhAAGBYYHjIGEAAYFhgeMgYQABgWGB4yBhAAGBYYHjIGEAAYFhgeMgYQABgWGB4yAhAmMh0QLhivARjHARiABBiOBRiXBRjcBBjeBBjgBNgBAUiuCVAAWABwAHgAkAEAmAFSoAFSqgEBMbgBA8gBAPgBAvgBAZgCAaACYZgDAOIDBRIBMSBAugYGCAEQARgUkgcBMaAHqQyyBwExuAdhwgcDMy0xyAcIgAgB&um=1&ie=UTF-8&fb=1&gl=us&sa=X&geocode=KSlQ0fnsPUyGMdwkqkxohmK7&daddr=8740+Ohio+Dr+A1,+Plano,+TX+75024" },
  { name: "Verona Villa", city: "Frisco", address: "6591 Dallas Pkwy, Frisco, TX 75034", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780829613748-w5kmf.jpg", mapsLink: "https://www.google.com/maps?sca_esv=8ffe236e7f802148&rlz=1C1GCEA_enUS1118US1120&sxsrf=ANbL-n5EmRXbXsq7P9XQQ4tgpTFKfrif0g:1780829023939&uact=5&gs_lp=Egxnd3Mtd2l6LXNlcnAiDFZlcm9uYSBWaWxsYTIUEC4YrwEYxwEYkQIYgAQYigUYjgUyBRAAGIAEMgUQABiABDILEC4YrwEYxwEYgAQyCxAuGK8BGMcBGIAEMgUQABiABDIFEAAYgAQyCxAuGK8BGMcBGIAEMgUQABiABDIFEAAYgAQyIxAuGK8BGMcBGJECGIAEGIoFGI4FGJcFGNwEGN4EGOAE2AEBSLgVUABYvxNwAHgAkAEAmAFRoAGrBqoBAjEyuAEDyAEA-AEBmAIMoAKMB8ICChAjGIAEGIoFGCfCAgQQIxgnwgILEAAYgAQYigUYkQLCAhEQLhiABBiKBRiRAhjHARivAcICERAuGIAEGLEDGIMBGMcBGNEDwgIIEAAYgAQYsQPCAg4QLhiABBixAxjHARjRA8ICDhAuGIAEGIoFGLEDGIMBwgIFEC4YgATCAhAQIxjwBRjJAhiABBiKBRgnwgIKECMY8AUYyQIYJ8ICChAAGIAEGIoFGEPCAg4QLhiDARixAxiABBiKBcICExAuGIAEGIoFGEMYxwEYrwEYjgXCAhAQLhiABBiKBRhDGMcBGK8BwgINEAAYgAQYigUYQxixA8ICExAuGEMYrwEYxwEYgAQYigUYjgXCAhAQLhiABBjHARivARiOBcICDRAuGIAEGIoFGEMYsQPCAg4QLhiABBjHARivARiOBcICCBAuGLEDGIAEwgILEC4YgAQYxwEYrwHCAgcQABiABBgKwgIiEC4YQxivARjHARiABBiKBRiOBRiXBRjcBBjeBBjgBNgBAcICHRAuGK8BGMcBGIAEGI4FGJcFGNwEGN4EGOAE2AEBmAMAugYGCAEQARgUkgcCMTKgB7_lAbIHAjEyuAeMB8IHBzAuMi42LjTIB0yACAE&um=1&ie=UTF-8&fb=1&gl=us&sa=X&geocode=KeHdr59xPEyGMURe6qFIsfbd&daddr=6591+Dallas+Pkwy,+Frisco,+TX+75034" },
  { name: "Grand Center", city: "Plano", address: "300 Chisholm Pl, Plano, TX 75075", website: "", image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&h=400&fit=crop", mapsLink: "https://www.google.com/maps/search/?api=1&query=300+Chisholm+Pl,+Plano,+TX+75075" },
  { name: "Minerva Banquet Plano", city: "Plano", address: "3825 W Spring Creek Pkwy Ste 207, Plano, TX 75023", website: "", image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-08/c28023d32593.jpeg", contactNumber: "4694478239" },
  { name: "Soul Arts and Events", city: "Frisco", address: "4681 Ohio Drive, Frisco, TX 75035", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783530792979-0ourw.png", mapsLink: "https://www.google.com/maps/search/?api=1&query=4681+Ohio+Drive,+Frisco,+TX+75035" },
  { name: "The Royal Banquet", city: "Irving", address: "1800 Market Pl Blvd, Irving, TX 75063", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784707996669-cu8jz9.png" },
  {
    name: "Lotus Springs Ranch",
    description: "Stunning Lakefront view and open Green Space",
    city: "McKinney",
    address: "4253 county rd 412 McKinney TX 75071",
    website: "",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-08-01/3c7e4fccf871.jpeg",
    mapsLink: "https://www.google.com/maps/search/?api=1&query=4253+county+rd+412+McKinney+TX+75071",
    instagram: "https://www.instagram.com/lotusspringsranch/",
  },
  {
    name: "Rotate Social",
    type: "Restaurants",
    city: "Frisco",
    address: "5454 Main St #123, Frisco, TX 75033",
    website: "",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782092538028-ubuxrf.jpg",
    mapsLink: "https://www.google.com/maps/search/?api=1&query=5454+Main+St+%23123,+Frisco,+TX+75033",
  },
  {
    name: "The Delhi Lounge",
    type: "Restaurants",
    city: "Irving",
    address: "1850 Market Pl Blvd, Irving, TX 75063",
    website: "",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782145243431-p5yi04.jpg",
    mapsLink: "https://www.google.com/maps/search/The+Delhi+Lounge+Irving+TX",
  },
  {
    name: "Ruchi Palace Banquet Hall",
    type: "Banquet Hall, Restaurants",
    city: "Carrollton",
    address: "3128 E Trinity Mls Rd, Carrollton, TX 75006, United States",
    website: "",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1787714473522-27i1kk.png",
    mapsLink: "https://www.google.com/maps/search/?api=1&query=3128+E+Trinity+Mls+Rd%2C+Carrollton%2C+TX+75006%2C+United+States",
  },
  {
    name: "Spice Rack Banquet Hall",
    type: "Banquet Hall, Restaurants",
    city: "Frisco",
    address: "10010 Coit Rd, Frisco, TX 75035",
    website: "",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783461078615-6lc4w.png",
    mapsLink: "https://www.google.com/maps/search/Spice+Rack+10010+Coit+Rd+Frisco+TX+75035",
  },
  {
    name: "Bawarchi Biryanis",
    type: "Banquet Hall, Restaurants",
    city: "Plano",
    address: "8201 Ohio Dr #110, Plano, TX 75024",
    website: "",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782144028810-1r3tzs.jpg",
    mapsLink: "https://www.google.com/maps/search/Bawarchi+Biryanis+8201+Ohio+Dr+Plano+TX+75024",
  },
  {
    name: "Moonrise Lake Lounge",
    type: "Restaurants",
    city: "Frisco",
    address: "8951 Lebanon Rd, Frisco, TX 75034",
    website: "",
    image: "https://images.unsplash.com/photo-1571896769668-a9e7d0b67b27?w=600&h=400&fit=crop",
    instagram: "https://www.instagram.com/p/Dbt7op9sYo7/",
  },
];

const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&h=400&fit=crop";

function mapsUrl(v: Venue) {
  return v.mapsLink ?? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${v.name}, ${v.address}`)}`;
}

export function VenueCard({ venue, index, isFeatured }: { venue: Venue; index: number; isFeatured?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div       className="w-full h-[110px] relative overflow-hidden">
        <img
          src={venue.image}
          alt={venue.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMAGE; }}
        />
      </div>
      <div className="p-2.5 pb-1 flex-1">
        <h3 className="text-[12px] font-bold text-gray-900 leading-tight mb-0.5">
          <Link to={`/party-venues/${slugify(venue.name)}`} className="hover:text-[#082f6d] hover:underline">
            {venue.name}
          </Link>
        </h3>
        {venue.description && (
          <p className="text-[10px] text-gray-500 leading-snug mb-1">{venue.description}</p>
        )}
        <div className="flex items-start gap-1 mb-0.5">
          <MapPin className="w-2.5 h-2.5 text-orange-500 shrink-0 mt-0.5" />
          <span className="text-[10px] text-gray-500 font-medium leading-snug">{venue.address}</span>
        </div>
        {isFeatured && (
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide">
            <span className="text-[#51ba4f]">Featured by TastebudsByAnubhi</span>
            <a
              href={TASTEBUDS_FEATURE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              aria-label="View feature on Instagram"
              className="text-orange-500 hover:text-orange-600 shrink-0"
            >
            <svg className="w-2.5 h-2.5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
            </svg>
            </a>
          </div>
        )}
      </div>
      <div className="px-2.5 pb-2.5 pt-1 flex flex-col gap-1.5">
        {venue.contactNumber && (
          <a
            href={`tel:${venue.contactNumber}`}
            className="flex items-center justify-center gap-1 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[10px] font-semibold py-1.5 rounded-md transition-colors duration-200"
          >
            <Phone className="w-2.5 h-2.5" /> Call
          </a>
        )}
        <SocialLinks item={{ ...venue, instagram: "", website: "", websiteUrl: "", websiteLink: "", website_url: "" }} px={20} />
        {(() => {
          const buttons: JSX.Element[] = [];
          if (venue.website) {
            buttons.push(
              <a
                key="website"
                href={venue.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-[10px] font-semibold py-1.5 rounded-md transition-colors duration-200 text-center shadow-sm"
              >
                <ExternalLink className="w-2.5 h-2.5" /> Website
              </a>
            );
          }
          buttons.push(
            <a
              key="location"
              href={mapsUrl(venue)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-[10px] font-semibold py-1.5 rounded-md transition-colors duration-200"
            >
              <ExternalLink className="w-2.5 h-2.5" /> {venue.website ? "Location" : "View Location"}
            </a>
          );
          if (venue.instagram) {
            buttons.push(
              <a
                key="instagram"
                href={venue.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1 bg-gradient-to-br from-pink-500 to-orange-400 hover:opacity-90 text-white text-[10px] font-semibold py-1.5 rounded-md transition-colors duration-200"
              >
                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                </svg>
                Instagram
              </a>
            );
          }
          return buttons.length > 1 ? (
            <div className="grid grid-cols-2 gap-1.5">{buttons}</div>
          ) : (
            buttons
          );
        })()}
      </div>
    </motion.div>
  );
}

const VENUES_TITLE = "Top Indian, Pakistani & South Asian Party Venues in Dallas–Fort Worth";

const VENUES_PARAGRAPHS = [
  "Discover the top Indian, Pakistani, and South Asian party venues, banquet halls, restaurants, and event spaces across the Dallas–Fort Worth area with DallasDesiScoop. Find venues for weddings, birthdays, engagements, anniversaries, baby showers, cultural celebrations, corporate events, and private parties.",
  "Explore banquet halls, restaurants with private event spaces, party halls, wedding venues, and restaurants that host celebrations in Dallas, Frisco, Plano, Irving, McKinney, Allen, The Colony, Prosper, Lewisville, Carrollton, Coppell, Richardson, Flower Mound, Little Elm, and surrounding DFW communities.",
  "From Indian and Pakistani banquet halls to restaurants offering private dining, party rooms, catering, and event spaces, discover venues for celebrations of every size.",
  "Whether you're looking for an Indian party venue, Pakistani event venue, South Asian banquet hall, restaurant party venue, wedding venue, or private event space near you, DallasDesiScoop helps you find the perfect place to celebrate across DFW.",
];

export default function Venues() {
  const [query, setQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const mc = useManagedCollection<Venue>("venues", VENUES, (v) => slugify(v.name));

  const filtered = mc.items.filter(
    (v) =>
      (query.trim() === "" || v.name.toLowerCase().includes(query.trim().toLowerCase())) &&
      (!selectedType ||
        (v.type ?? "Banquet Hall")
          .split(",")
          .map((t) => t.trim())
          .includes(selectedType))
  );

  const featuredVenues = filtered.filter((v) => FEATURED_VENUE_NAMES.has(v.name.trim().toLowerCase()));
  const nonFeaturedVenues = filtered.filter((v) => !FEATURED_VENUE_NAMES.has(v.name.trim().toLowerCase()));

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Party Venues & Event Venues in Dallas–Fort Worth"
        placeholder="Search party venues…"
        query={query}
        setQuery={setQuery}
        backgroundImage="https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-20/2bc81e6ff61d.jpeg"
      />
      <NewsTicker />

      <CategoryNav />

      <section className="bg-gray-50/40 py-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          {/* LEFT: venues */}
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">{VENUES_TITLE}</h2>
            <p className="text-[13px] text-gray-500 mb-1">{VENUES_PARAGRAPHS[0]}</p>
            {descExpanded && (
              <>
                {VENUES_PARAGRAPHS.slice(1).map((p, i) => (
                  <p key={i} className="text-[13px] text-gray-500 mb-1">{p}</p>
                ))}
              </>
            )}
            <button
              type="button"
              onClick={() => setDescExpanded((v) => !v)}
              className="sm:hidden text-[12px] font-semibold text-[#082f6d] mb-4"
            >
              {descExpanded ? "Read less" : "Read more"}
            </button>
            <div className="hidden sm:block">
              {VENUES_PARAGRAPHS.slice(1).map((p, i) => (
                <p key={i} className="text-[13px] text-gray-500 mb-1">{p}</p>
              ))}
            </div>
            <div className="mb-4" />
            <div className="flex flex-wrap gap-2 mb-5">
              <FilterChip active={selectedType === null} onClick={() => setSelectedType(null)} color="#51ba4f">
                All
              </FilterChip>
              {VENUE_TYPE_OPTIONS.map((type) => (
                <FilterChip key={type} active={selectedType === type} onClick={() => setSelectedType(selectedType === type ? null : type)} color="#51ba4f">
                  {type}
                </FilterChip>
              ))}
            </div>
            <AdminAddBar mc={mc} fields={VENUE_FIELDS} label="Add Party Venue" />

            {featuredVenues.length > 0 && (
              <div className="mb-8">
                <h3 className="text-[14px] sm:text-[16px] font-bold text-green-700 mb-3">
                  Featured by TastebudsByAnubhi
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {featuredVenues.map((v, i) => (
                    <AdminItem key={v._key} mc={mc} item={v} fields={VENUE_FIELDS} title="Party Venue">
                      <VenueCard venue={v} index={i} isFeatured />
                    </AdminItem>
                  ))}
                </div>
              </div>
            )}

            {nonFeaturedVenues.length > 0 ? (
              <div>
                {featuredVenues.length > 0 && (
                  <h3 className="text-[14px] sm:text-[16px] font-bold text-gray-700 mb-3">
                    More Party Venues
                  </h3>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {nonFeaturedVenues.map((v, i) => (
                    <AdminItem key={v._key} mc={mc} item={v} fields={VENUE_FIELDS} title="Party Venue">
                      <VenueCard venue={v} index={i} />
                    </AdminItem>
                  ))}
                </div>
              </div>
            ) : featuredVenues.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No party venues match your filters.
              </div>
            ) : null}
          </div>

          
        </div>
      </section>
    </div>
  );
}
